<template>
  <div class="alert-overview-section">
    <!-- 头部 -->
    <div class="overview-header">
      <h3>今日告警智能摘要</h3>
    </div>

    <!-- 内容 -->
    <div class="overview-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>正在生成智能摘要...</p>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="error-state">
        <span class="error-icon">⚠️</span>
        <p>{{ error }}</p>
      </div>

      <!-- 数据展示 -->
      <div v-else-if="overviewData" class="overview-data">
        <!-- 统计卡片 -->
        <div class="stats-cards">
          <div class="stat-card stat-high" :class="{ 'active': activeCategory === 'safety' }"
            @click="selectCategory('safety')">
            <div class="stat-count">{{ overviewData.summary?.safetyImpactCount || 0 }}</div>
            <div class="stat-label">影响安全</div>
          </div>
          <div class="stat-card stat-revenue" :class="{ 'active': activeCategory === 'revenue' }"
            @click="selectCategory('revenue')">
            <div class="stat-count">{{ overviewData.summary?.incomeImpactCount || 0 }}</div>
            <div class="stat-label">影响收益</div>
          </div>
          <div class="stat-card stat-repeat" :class="{ 'active': activeCategory === 'repeated' }"
            @click="selectCategory('repeated')">
            <div class="stat-count">{{ overviewData.summary?.repeatCount || 0 }}</div>
            <div class="stat-label">重复发生</div>
          </div>
          <div class="stat-card stat-unrecovered" :class="{ 'active': activeCategory === 'unrecovered' }"
            @click="selectCategory('unrecovered')">
            <div class="stat-count">{{ overviewData.summary?.unrecoveredCount || 0 }}</div>
            <div class="stat-label">未恢复</div>
          </div>
        </div>

        <!-- 智能摘要文本 -->
        <div v-if="overviewData.summaryText" class="summary-text">
          <div class="summary-icon">💡</div>
          <p>{{ overviewData.summaryText }}</p>
        </div>

        <!-- 告警列表（根据选中的类别显示） -->
        <div class="alarm-section">
          <h4 v-if="alarmLoading || currentAlarms?.length">{{ categoryTitle }} <span class="alarm-count">{{ alarmTotal
          }}</span></h4>
          <div v-if="alarmLoading" class="alarm-loading">加载中...</div>
          <div v-else-if="alarmError" class="alarm-error">{{ alarmError }}</div>
          <div v-else-if="currentAlarms?.length" class="alarm-list-scroll" @scroll="handleListScroll">
            <div class="alarm-list">
              <div v-for="item in currentAlarms" :key="item.alarmId" class="alarm-item"
                :class="getLevelClass(item.level)" @click="handleAnalyzeAlarm(item)">
                <!-- 左侧等级色条 -->
                <span class="alarm-bar"></span>

                <div class="alarm-body">
                  <!-- 第一行：设备名 + UUID + 等级标签 + 触发次数 -->
                  <div class="alarm-top">
                    <span class="alarm-device-name" :title="item.equipmentName">{{ item.equipmentName }}</span>
                    <span class="alarm-uuid">{{ item.uuid }}</span>
                    <span class="alarm-level-badge">{{ getLevelText(item.level) }}</span>
                    <span class="alarm-trigger" v-if="item.triggerCount > 1">×{{ item.triggerCount }}</span>
                  </div>

                  <!-- 第二行：告警描述 -->
                  <div class="alarm-desc">{{ item.description }}</div>

                  <!-- 第三行：编码 -->
                  <div class="alarm-tags">
                    <span class="alarm-tag" v-if="item.alarmCode">告警 {{ item.alarmCode }}</span>
                  </div>

                  <!-- 第四行：时间 -->
                  <div class="alarm-time">{{ item.lastTime }}</div>
                </div>

                <span class="alarm-arrow">›</span>
              </div>

              <!-- 底部加载状态 -->
              <div v-if="loadingMore" class="list-footer-tip">加载中...</div>
              <div v-else-if="!hasMore" class="list-footer-tip">没有更多了</div>
            </div>
          </div>

          <!-- 空状态 -->
          <div v-else class="empty-state">
            <div class="empty-icon">📋</div>
            <p class="empty-text">暂无{{ categoryTitle }}告警</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { getAlarmDashboardOverview, getAlarmDashboardPriorityList } from '@/api/alarm'
import { getAlarmLevelText } from '../../dicts/alert'

const props = defineProps({
  stationId: {
    type: String,
    default: ''
  },
  // 触发刷新的标志
  refreshFlag: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['analyze-alarm'])

// 加载状态
const loading = ref(false)
const error = ref('')
const overviewData = ref(null)

// 当前选中的类别
const activeCategory = ref('') // safety, revenue, repeated, unrecovered

// 告警列表
const alarmList = ref([])
const alarmTotal = ref(0)
const alarmLoading = ref(false)
const alarmError = ref('')

// 分页 - 下拉加载更多
const PAGE_SIZE = 15
const pageNum = ref(1)
const hasMore = ref(false)
const loadingMore = ref(false)

// 类别标题映射
const categoryTitles = {
  safety: '影响安全',
  revenue: '影响收益',
  repeated: '重复发生',
  unrecovered: '未恢复'
}

// 类别 → 接口 category 枚举映射
const categoryEnumMap = {
  safety: 'SAFETY_IMPACT',
  revenue: 'INCOME_IMPACT',
  repeated: 'REPEAT',
  unrecovered: 'UNRECOVERED'
}

// 当前类别标题
const categoryTitle = computed(() => categoryTitles[activeCategory.value] || '影响安全')

// 当前显示的告警列表
const currentAlarms = computed(() => alarmList.value)

// 选择类别
const selectCategory = (category) => {
  if (activeCategory.value === category) return
  activeCategory.value = category
  fetchAlarmList()
}

// 获取告警等级文本
const getLevelText = (level) => {
  return getAlarmLevelText(level)
}

// 获取等级样式类
const getLevelClass = (level) => {
  const levelMap = {
    0: 'level-info',
    1: 'level-low',
    2: 'level-medium',
    3: 'level-high',
    4: 'level-critical'
  }
  return levelMap[level] || 'level-info'
}

// 获取今日告警总览
const fetchOverview = async () => {
  loading.value = true
  error.value = ''
  overviewData.value = null

  try {
    const result = await getAlarmDashboardOverview({
      stationId: props.stationId
    })

    if (result && result.code === 200) {
      overviewData.value = result.data

      // 自动选中第一个有数据的卡片
      if (!activeCategory.value) {
        autoSelectFirstCategory()
      } else {
        fetchAlarmList()
      }
    } else {
      throw new Error(result?.msg || '获取告警总览失败')
    }
  } catch (err) {
    console.error('获取告警总览失败:', err)
    error.value = err.message || '智能摘要服务暂时不可用，请稍后重试'
  } finally {
    loading.value = false
  }
}

// 获取分类告警列表（首屏 / 切换分类时重置）
const fetchAlarmList = async () => {
  if (!props.stationId || !activeCategory.value) return

  alarmLoading.value = true
  alarmError.value = ''
  alarmList.value = []
  alarmTotal.value = 0
  pageNum.value = 1
  hasMore.value = false

  try {
    const result = await getAlarmDashboardPriorityList({
      stationId: props.stationId,
      category: categoryEnumMap[activeCategory.value],
      pageNum: pageNum.value,
      pageSize: PAGE_SIZE
    })

    if (result && result.code === 200) {
      const data = result.data || {}
      alarmList.value = data.list || []
      alarmTotal.value = data.total ?? 0
      hasMore.value = alarmList.value.length < alarmTotal.value
    } else {
      throw new Error(result?.msg || '获取告警列表失败')
    }
  } catch (err) {
    console.error('获取告警列表失败:', err)
    alarmError.value = err.message || '告警列表服务暂时不可用，请稍后重试'
    alarmList.value = []
    alarmTotal.value = 0
  } finally {
    alarmLoading.value = false
  }
}

// 下拉加载更多
const loadMoreAlarmList = async () => {
  if (loadingMore.value || alarmLoading.value || !hasMore.value || !props.stationId) return

  loadingMore.value = true
  pageNum.value += 1

  try {
    const result = await getAlarmDashboardPriorityList({
      stationId: props.stationId,
      category: categoryEnumMap[activeCategory.value],
      pageNum: pageNum.value,
      pageSize: PAGE_SIZE
    })

    if (result && result.code === 200) {
      const data = result.data || {}
      const records = data.list || []
      alarmList.value = alarmList.value.concat(records)
      hasMore.value = alarmList.value.length < alarmTotal.value
    } else {
      throw new Error(result?.msg || '加载更多失败')
    }
  } catch (err) {
    console.error('加载更多失败:', err)
    // 加载失败回退页码，允许重试
    pageNum.value = Math.max(1, pageNum.value - 1)
  } finally {
    loadingMore.value = false
  }
}

// 列表滚动：触底加载更多
const handleListScroll = (e) => {
  const target = e.target
  const { scrollTop, scrollHeight, clientHeight } = target
  if (scrollHeight - scrollTop - clientHeight < 80) {
    loadMoreAlarmList()
  }
}

// 自动选中第一个有数据的卡片
const autoSelectFirstCategory = () => {
  const summary = overviewData.value?.summary

  // 按顺序检查哪个类别有数据
  const categories = [
    { key: 'safety', count: summary?.safetyImpactCount || 0 },
    { key: 'revenue', count: summary?.incomeImpactCount || 0 },
    { key: 'repeated', count: summary?.repeatCount || 0 },
    { key: 'unrecovered', count: summary?.unrecoveredCount || 0 }
  ]

  // 找到第一个有数据的类别
  const firstCategory = categories.find(cat => cat.count > 0)

  if (firstCategory) {
    activeCategory.value = firstCategory.key
  } else {
    // 如果都没有数据，默认选中第一个
    activeCategory.value = 'safety'
  }

  fetchAlarmList()
}

// 触发智能分析
const handleAnalyzeAlarm = (alarm) => {
  emit('analyze-alarm', alarm)
}

// 监听 stationId 变化，重新获取
watch(() => props.stationId, () => {
  if (props.stationId) {
    fetchOverview()
  }
})

// 监听刷新标志
watch(() => props.refreshFlag, () => {
  if (props.refreshFlag > 0) {
    fetchOverview()
  }
})

// 组件挂载时获取
onMounted(() => {
  if (props.stationId) {
    fetchOverview()
  }
})
</script>

<style scoped lang="scss">
.alert-overview-section {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fff;
  flex-shrink: 0;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #1d2433;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    margin: 0;
  }
}

.overview-content {
  padding: 20px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.overview-data {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

// 加载状态
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;

  p {
    margin: 12px 0 0 0;
    color: #667085;
    font-size: 13px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3a68ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.error-state {
  .error-icon {
    font-size: 32px;
  }

  p {
    color: #f5222d;
  }
}

// 统计卡片
.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.stat-card {
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &.active {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
    transform: translateY(-4px);
  }

  .stat-count {
    font-size: 32px;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 10px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  .stat-label {
    font-size: 13px;
    font-weight: 500;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  &.stat-high {
    background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);

    .stat-count {
      color: #f5222d;
    }

    .stat-label {
      color: #cf1322;
    }

    &.active {
      border-color: #ff7875;
    }
  }

  &.stat-revenue {
    background: linear-gradient(135deg, #fffbf0 0%, #ffe7ba 100%);

    .stat-count {
      color: #fa8c16;
    }

    .stat-label {
      color: #d46b08;
    }

    &.active {
      border-color: #ffa940;
    }
  }

  &.stat-repeat {
    background: linear-gradient(135deg, #f0f9ff 0%, #bae7ff 100%);

    .stat-count {
      color: #1890ff;
    }

    .stat-label {
      color: #096dd9;
    }

    &.active {
      border-color: #40a9ff;
    }
  }

  &.stat-unrecovered {
    background: linear-gradient(135deg, #faf4ff 0%, #e4d4f4 100%);

    .stat-count {
      color: #722ed1;
    }

    .stat-label {
      color: #531dab;
    }

    &.active {
      border-color: #9254de;
    }
  }
}

// 智能摘要文本
.summary-text {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8faff;
  border-left: 4px solid #3a68ff;
  border-radius: 8px;
  margin-bottom: 20px;
  flex-shrink: 0;

  .summary-icon {
    font-size: 20px;
    flex-shrink: 0;
  }

  p {
    margin: 0;
    color: #344054;
    font-size: 14px;
    line-height: 1.7;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }
}

// 告警列表
.alarm-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  h4 {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    color: #1d2433;
    margin: 0 0 10px 0;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    flex-shrink: 0;

    .alarm-count {
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #eef1f6;
      color: #667085;
      border-radius: 9px;
      font-size: 11px;
      font-weight: 600;
    }
  }
}

.alarm-list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d5dd;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.alarm-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.list-footer-tip {
  grid-column: 1 / -1;
  padding: 16px 0;
  text-align: center;
  color: #98a2b3;
  font-size: 12px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
}

.alarm-loading,
.alarm-error {
  padding: 40px 20px;
  text-align: center;
  color: #667085;
  font-size: 13px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
}

.alarm-error {
  color: #f5222d;
}

.alarm-item {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 16px 16px 16px 10px;
  background: #fff;
  border: 1px solid #edf0f4;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  overflow: hidden;

  &:hover {
    border-color: #c7d6ff;
    box-shadow: 0 2px 10px rgba(58, 104, 255, 0.1);

    .alarm-arrow {
      color: #3a68ff;
      transform: translateX(2px);
    }
  }

  // 左侧等级色条
  .alarm-bar {
    width: 3px;
    border-radius: 2px;
    flex-shrink: 0;
    background: #d0d5dd;
  }

  .alarm-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  // 第一行
  .alarm-top {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .alarm-device-name {
    flex: 1;
    min-width: 0;
    color: #1d2433;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  .alarm-level-badge {
    flex-shrink: 0;
    padding: 1px 7px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    line-height: 16px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  .alarm-trigger {
    flex-shrink: 0;
    padding: 1px 6px;
    background: #fff2e8;
    color: #fa541c;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    line-height: 16px;
  }

  // 第二行 描述
  .alarm-desc {
    color: #475467;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  // 第三行 标签
  .alarm-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
    overflow: hidden;
  }

  .alarm-tag {
    flex-shrink: 0;
    padding: 1px 6px;
    background: #f2f4f7;
    color: #667085;
    border-radius: 4px;
    font-size: 11px;
    line-height: 16px;
    font-family: 'Consolas', 'Monaco', monospace;
    white-space: nowrap;
  }

  .alarm-uuid {
    min-width: 0;
    color: #98a2b3;
    font-size: 11px;
    line-height: 16px;
    font-family: 'Consolas', 'Monaco', monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // 第四行 时间
  .alarm-time {
    color: #98a2b3;
    font-size: 11px;
    line-height: 1.2;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  .alarm-arrow {
    align-self: center;
    flex-shrink: 0;
    color: #cbd2dc;
    font-size: 20px;
    font-weight: 400;
    transition: color 0.15s, transform 0.15s;
  }

  // 等级配色（作用于色条与标签）
  &.level-high,
  &.level-critical {
    .alarm-bar {
      background: #f5222d;
    }

    .alarm-level-badge {
      background: #fff1f0;
      color: #f5222d;
    }
  }

  &.level-medium {
    .alarm-bar {
      background: #fa8c16;
    }

    .alarm-level-badge {
      background: #fff7e6;
      color: #fa8c16;
    }
  }

  &.level-low {
    .alarm-bar {
      background: #faad14;
    }

    .alarm-level-badge {
      background: #fffbe6;
      color: #d48806;
    }
  }

  &.level-info {
    .alarm-bar {
      background: #13c2c2;
    }

    .alarm-level-badge {
      background: #e6fffb;
      color: #08979c;
    }
  }
}

// 空状态
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }

  .empty-text {
    margin: 0;
    color: #98a2b3;
    font-size: 14px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }
}

// 响应式适配
@media screen and (max-width: 1366px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .alarm-list {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 768px) {
  .overview-header {
    padding: 12px 16px;

    h3 {
      font-size: 14px;
    }
  }

  .overview-content {
    padding: 16px;
  }

  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .stat-card {
    padding: 12px;

    .stat-count {
      font-size: 24px;
    }

    .stat-label {
      font-size: 12px;
    }
  }

  .alarm-list {
    grid-template-columns: 1fr;
  }
}
</style>
